import { Check, Users, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Button,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui";
import { useTenantUserSearch } from "~/features/admin/tenants/hooks";
import type { FilterUserOption } from "~/features/admin/tenants/types";
import { cn } from "~/lib/utils";

interface TenantUserFilterProps {
  selectedUsers: FilterUserOption[];
  onChange: (users: FilterUserOption[]) => void;
}

/** Filter the tenant list by member. Debounced async user search inside a popover. */
export function TenantUserFilter({ selectedUsers, onChange }: TenantUserFilterProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { options, loading, search, reset } = useTenantUserSearch();

  const hasFilter = selectedUsers.length > 0;
  const triggerLabel = !hasFilter
    ? t("ui.filterByX", { x: t("model.user.modelName") })
    : selectedUsers.length === 1
      ? (selectedUsers[0].displayName ?? selectedUsers[0].email)
      : `${selectedUsers.length} ${t("model.user.modelNames")}`;

  const toggle = (option: FilterUserOption) => {
    const exists = selectedUsers.some((u) => u.id === option.id);
    onChange(exists ? selectedUsers.filter((u) => u.id !== option.id) : [...selectedUsers, option]);
  };

  return (
    <div className="inline-flex items-center gap-1">
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) reset();
        }}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={cn("max-w-[220px]", hasFilter && "border-primary-500 text-primary-700")}
          >
            <Users className={cn(hasFilter && "fill-primary-100")} />
            <span className="truncate">{triggerLabel}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="center" className="w-[320px] p-0">
          {selectedUsers.length > 0 && (
            <div className="flex flex-wrap gap-1 border-b border-border-default p-2">
              {selectedUsers.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => toggle(u)}
                  className="inline-flex items-center gap-1 rounded-pill bg-bg-subtle px-2 py-0.5 text-12-regular text-fg-2 hover:bg-primary-50 hover:text-primary-700"
                >
                  <span className="max-w-[180px] truncate">{u.displayName ?? u.email}</span>
                  <X className="h-3 w-3" />
                </button>
              ))}
            </div>
          )}
          <Command shouldFilter={false}>
            <CommandInput placeholder={t("ui.search")} onValueChange={search} />
            <CommandList>
              {!loading && <CommandEmpty>{t("feature.tenants.noUsersFound")}</CommandEmpty>}
              {options.map((option) => {
                const selected = selectedUsers.some((u) => u.id === option.id);
                return (
                  <CommandItem key={option.id} value={option.id} onSelect={() => toggle(option)}>
                    <Check className={cn("h-4 w-4", selected ? "opacity-100" : "opacity-0")} />
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate text-fg-1">{option.displayName ?? option.email}</span>
                      {option.displayName && <span className="truncate text-12-regular text-fg-3">{option.email}</span>}
                    </span>
                  </CommandItem>
                );
              })}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {hasFilter && (
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={() => onChange([])}
          aria-label={t("ui.clearAll")}
          className="border-primary-500 text-primary-700"
        >
          <X />
        </Button>
      )}
    </div>
  );
}
